// The yardage estimate behind the HUD's "est. carry". It runs the *shared* physics — the same code
// the Worker runs — over four powers per club for the current lie, once, and interpolates between
// them while the meter moves. It is labelled an estimate in the HUD because the wind is seeded per
// stroke: the number is what this club would do with this wind, not a promise about the next shot.
// Nothing here is ever sent to the server; the server still simulates the shot that counts.
//
// QC round 2: the chart used to store totalM, which physics sets to 0 for a ball out of bounds
// (final = start), so a full wedge over the fence read "0 m" and the readout fell as the meter
// rose; and the meter was mapped onto the samples as if 0 % were the first one, so every number
// was biased high. It now stores the carry the label promises, keeps the penalty beside it, and
// interpolates on the real power axis.

import type { Hole } from '@shared/course';
import { CLUB_ORDER, aimTo, simulateShot, windSeedFor, type Club } from '@shared/physics';

export interface CarrySample { power: number; carryM: number; penalty: number }
export type CarryChart = Record<Club, CarrySample[]>;

const POWERS = [0.25, 0.5, 0.75, 1.0];

export function carryChart(hole: Hole, from: { x: number; z: number }, strokeIndex: number): CarryChart {
  const windSeed = windSeedFor(hole, strokeIndex);
  const aimDeg = aimTo(from, hole.cup);
  const chart = {} as CarryChart;
  for (const club of CLUB_ORDER) {
    chart[club] = POWERS.map((power) => {
      const r = simulateShot(hole, { club, aimDeg, power, spin: 0, from, windSeed });
      // a putt does not carry; its number is the roll
      return { power, carryM: club === 'putter' ? r.totalM : r.carryM, penalty: r.penalty };
    });
  }
  return chart;
}

export interface Estimate { metres: number; penalty: boolean }

/** Linear between the sampled powers on the power axis (held flat below the first sample);
 *  null if there is no chart for this lie yet. `penalty` says the nearer sample left the
 *  property, so the HUD can say so instead of printing a number that will not happen. */
export function estimateFor(chart: CarryChart | null, club: Club, power: number): Estimate | null {
  const row = chart?.[club];
  if (!row || !row.length) return null;
  const p = Math.max(0, Math.min(1, power));
  if (p <= row[0].power) return { metres: row[0].carryM, penalty: row[0].penalty > 0 };
  let i = 0;
  while (i < row.length - 2 && p > row[i + 1].power) i++;
  const a = row[i], b = row[i + 1];
  const f = (p - a.power) / (b.power - a.power);
  const near = f < 0.5 ? a : b;
  return { metres: a.carryM + (b.carryM - a.carryM) * f, penalty: near.penalty > 0 };
}
